import {
  AssessmentStageActionKind,
  DeiStageActionFragment,
  StageActionAvailability,
  StageAvailableActionFragment,
} from 'generated/graphql';
import { FC, useMemo, useState } from 'react';
import {
  Button,
  Modal,
  SelectFormField,
  TSelectOption,
} from '@spotted-zebra-uk/ui-components';
import AddSkillsSelfAssessmentAction from './AddSkillsSelfAssessmentAction';
import AddTestAction, { isTestActionAvailable } from './AddTestAction';
import {
  deiInitialValues,
  getPrefilledDeiFields,
} from './DeiActionForm/dei.helpers';
import DeiActionForm from './DeiActionForm/DeiActionForm';

type TStageActionModalMode =
  | 'add-skills-self-assessment-action'
  | 'add-dei-action'
  | 'add-personality-test-action'
  | 'add-cognitive-test-action';

const COGNITIVE_TEST_ACTION_KINDS = [
  AssessmentStageActionKind.CognitiveTestInductive,
  AssessmentStageActionKind.CognitiveTestNumerical,
  AssessmentStageActionKind.CognitiveTestVerbal,
];

interface IStageActionModal {
  stageId: string;
  projectId: number;
  deiData: DeiStageActionFragment | null | undefined;
  availableActions: StageAvailableActionFragment[];
  onClose: () => void;
}

const StageActionModal: FC<IStageActionModal> = ({
  onClose,
  stageId,
  deiData,
  projectId,
  availableActions,
}) => {
  const [mode, setMode] = useState<TSelectOption | undefined>();

  const handleModeChange = (newValue: TSelectOption) => {
    setMode(newValue);
  };

  const softSkillAssessmentAvailability = availableActions.find(
    actionAvailability =>
      actionAvailability.kind === AssessmentStageActionKind.RssAssessmentSoft
  );

  const technicalSkillAssessmentAvailability = availableActions.find(
    actionAvailability =>
      actionAvailability.kind ===
      AssessmentStageActionKind.RssAssessmentTechnical
  );

  const hasDeiForm = deiData && !deiData.deletedAt;
  const hasArchivedDeiForm = Boolean(deiData?.deletedAt);

  const selectActionTypeOptions = [
    {
      label: 'Personality test',
      value: 'add-personality-test-action' as TStageActionModalMode,
      isDisabled: !isTestActionAvailable(
        availableActions,
        AssessmentStageActionKind.PersonalityTest
      ),
    },
    {
      label: 'Cognitive test',
      value: 'add-cognitive-test-action' as TStageActionModalMode,
      isDisabled: !COGNITIVE_TEST_ACTION_KINDS.some(kind =>
        isTestActionAvailable(availableActions, kind)
      ),
    },
    {
      label: 'Skill self-assessment',
      value: 'add-skills-self-assessment-action' as TStageActionModalMode,
      isDisabled:
        softSkillAssessmentAvailability?.availability !==
          StageActionAvailability.Available &&
        technicalSkillAssessmentAvailability?.availability !==
          StageActionAvailability.Available,
    },
    {
      label: 'Demographic information',
      value: 'add-dei-action' as TStageActionModalMode,
      isDisabled: Boolean(hasDeiForm),
    },
  ];

  const prefilledDeiData = useMemo(
    () => (deiData ? getPrefilledDeiFields(deiData?.fields) : []),
    [deiData]
  );

  return (
    <Modal
      header="Add action"
      isOpen
      onClose={onClose}
      className="add-stage-action-modal"
    >
      <div
        className="add-stage-action-modal__header"
        data-testid="add-stage-action-modal__action-select"
      >
        <SelectFormField
          options={selectActionTypeOptions}
          onChange={handleModeChange}
          id="stage-action-modal-mode-select"
          value={mode}
          label="Action"
          placeholder="Action"
        />
      </div>
      {mode?.value === 'add-skills-self-assessment-action' && (
        <AddSkillsSelfAssessmentAction
          softSkillAssessmentAvailability={softSkillAssessmentAvailability}
          technicalSkillAssessmentAvailability={
            technicalSkillAssessmentAvailability
          }
          stageId={stageId}
          projectId={projectId}
          onClose={onClose}
        />
      )}
      {mode?.value === 'add-dei-action' && (
        <DeiActionForm
          stageId={stageId}
          projectId={projectId}
          onClose={onClose}
          type="create"
          initialValues={
            hasArchivedDeiForm
              ? { deiValues: prefilledDeiData }
              : deiInitialValues
          }
          hasArchivedDeiForm={hasArchivedDeiForm}
        />
      )}
      {(mode?.value === 'add-personality-test-action' ||
        mode?.value === 'add-cognitive-test-action') && (
        <AddTestAction
          stageId={stageId}
          projectId={projectId}
          mode={
            mode.value === 'add-personality-test-action'
              ? 'personality'
              : 'cognitive'
          }
          availableActions={availableActions}
          onClose={onClose}
        />
      )}
      {!mode && (
        <div className="add-stage-action-modal__actions">
          <Button onClick={onClose} size="medium" variant="secondary">
            Cancel
          </Button>
          <Button size="medium" disabled>
            Add action
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default StageActionModal;
